import { isClientApp, STUDIOS } from '../../app/ClientApp';
import { icsHref, mapsUrl } from './calendar';
import { bookingComment, coveredBy, type BookingMembership } from './membership';
import { bookableDays, dayStrip } from './days';
import {
  Fragment,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import {
  LOCALE_NAMES,
  PAYMENT_METHODS,
  eligibleGifts,
  formatMoney,
  giftBaseMinor,
  localize,
  nextGiftStep,
  type BookingGiftOption,
  type ClientMembership,
  type Locale,
  type PaymentMethod,
  type ProductOffer,
  type Service,
  type ServiceKind,
  type Staff,
} from '@360face/domain';
import { ApiError, type HubApi, type KioskVisit } from '../../api/client';
import { BRAND } from '../../brand';
import { BrandSplash } from '../../splash/BrandSplash';
import { endTime, formatDay, formatDayLong } from '../../i18n/format';
import { MESSAGES, errorText, type Messages } from '../../i18n/messages';
import { ClientForm } from '../../kiosk/ClientForm';
import { kioskToday, type CustomerField } from '../../kiosk/flow';
import { KInput, KeyboardProvider, KeyboardSlot, useKeyboardClose } from '../../kiosk/Keyboard';
import {
  BookingLine,
  CalendarIcon,
  CheckIcon,
  GiftIcon,
  GlobeIcon,
  Load,
  PAY_ICON,
  PhoneCallIcon,
  QrIcon,
  SubmitButton,
  month,
  useKioskConfig,
  useStaff,
  weekday,
} from '../../kiosk/shared';
import { PhoneInput } from '../../kiosk/PhoneInput';
import { toE164 } from '../../kiosk/phone';
import { createScanBuffer } from '../../kiosk/scanner';
import {
  addonsFor,
  dayStaffLabel,
  serviceCards,
  slotsByPart,
  staffByDay,
  suggestedExtras,
  visibleSlots,
  type ServiceCard,
} from './catalog';
import {
  BOOKING_STEPS,
  DONE_SECONDS,
  IDLE_GRACE_SECONDS,
  IDLE_WARN_SECONDS,
  MAX_ADDONS,
  bookingStep,
  canGoBack,
  contactErrors,
  knownYes,
  v2Initial,
  v2Reducer,
  type V2Event,
  type V2State,
  type V2Step,
} from './flow';
import { V2_MESSAGES, type V2Messages } from './messages';
import { AssistCall } from './AssistCall';
import { assistMirror } from './assist';
import { PAY_ART, V3_ART, extraArt, serviceArt } from './art';
import { MuscleMap } from './muscles/MuscleMap';
import { muscleCard } from './muscles/muscles';
import '../v2.css';
import '../v3.css';

type Dispatch = (e: V2Event) => void;

/**
 * Kiosk v3 (owner 23.10.2026): the same kiosk (flow, API, texts) in the style of the client-app
 * mockup — light panels, 3D pictures, numbered steps, rounder cards. Only the look differs; /v3.
 */
const V3 = createContext(false);
/** The membership the client books with, from the app (owner 26.10.2026). */
const Mem = createContext<BookingMembership | undefined>(undefined);
const useV3 = () => useContext(V3);
/** v3: opens the "Learn more" page of a treatment or extra (owner 23.10.2026). */
/** A card with several lengths (Face Workout) opens one page: the card title and all its lengths. */
type InfoGroup = { title?: Service['title']; variants?: Service[] };
const InfoOpen = createContext<(s: Service, group?: InfoGroup) => void>(() => undefined);

const LEARN_MORE: Record<Locale, string> = {
  pt: 'Saber mais',
  en: 'Learn more',
  fr: 'En savoir plus',
  es: 'Saber más',
  ru: 'Подробнее',
  uk: 'Детальніше',
  de: 'Mehr erfahren',
};

/** The small "Learn more" button in the corner of a v3 card; never selects the card itself. */
function InfoButton({ service, group, locale }: { service: Service; group?: InfoGroup; locale: Locale }) {
  const open = useContext(InfoOpen);
  return (
    <button
      type="button"
      className="v3-info"
      aria-label={`${LEARN_MORE[locale]}: ${localize(group?.title ?? service.title, locale)}`}
      onClick={(e) => {
        e.stopPropagation();
        open(service, group);
      }}
    >
      <span aria-hidden>i</span>
      {LEARN_MORE[locale]}
    </button>
  );
}

/**
 * "Learn more" page (owner 24.10.2026): text only — name, length and price, what is included, and for
 * the massages which muscles we focus on. The fuller "what is included" texts come from the catalogue.
 */
function InfoSheet({
  service,
  group,
  locale,
  m,
  onClose,
}: {
  service: Service;
  group?: InfoGroup;
  locale: Locale;
  m: Messages;
  onClose: () => void;
}) {
  const muscles = muscleCard(service.title);
  const title = localize(group?.title ?? service.title, locale);
  const lengths = group?.variants && group.variants.length > 1 ? group.variants : undefined;
  return (
    <div className="v2-backdrop" onClick={onClose}>
      <div
        className="v2-sheet v3-info-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <h2>{title}</h2>
        {lengths ? (
          <ul className="v3-info-lengths">
            {lengths.map((x) => (
              <li key={x.id}>
                <b>{localize(x.title, locale)}</b> · {x.durationMin} {m.minutes} · {formatMoney(x.price, locale)}
              </li>
            ))}
          </ul>
        ) : (
          <p className="v3-info-meta">
            {service.durationMin} {m.minutes} · {formatMoney(service.price, locale)}
          </p>
        )}
        {!muscles && service.description && <p className="v3-info-text">{localize(service.description, locale)}</p>}
        {muscles && <MuscleMap card={muscles} locale={locale} />}
        <button className="v2-btn v2-btn-ink" onClick={onClose}>
          {m.back}
        </button>
      </div>
    </div>
  );
}
const KIND_ORDER: ServiceKind[] = ['face', 'body', 'skin', 'combo'];

/**
 * Kiosk v2 (owner 16.10.2026): same API and data as v1, different flow and look, at /v2.
 * One primary action per screen, in the dark rail at the bottom; the booking is 5 steps;
 * "I have a booking" is the main door. See the "analysis and v2 changes" document.
 */
/** A phone-sized screen (same breakpoint as the phone styles in v2.css). */
function useIsPhone(): boolean {
  const query = '(max-width: 600px)';
  const [phone, setPhone] = useState(
    () => typeof window !== 'undefined' && window.matchMedia?.(query).matches === true,
  );
  useEffect(() => {
    const mq = window.matchMedia?.(query);
    if (!mq) return;
    const on = () => setPhone(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return phone;
}

/**
 * Is this the studio kiosk? (owner 17.10.2026: clients' own Android/iOS phones and tablets must get
 * their own keyboard.) Yes when opened from the home-screen icon (standalone, as set up on the
 * kiosk iPad) or once marked with ?kiosk=1 (remembered on that device; ?kiosk=0 forgets it).
 */
function isStudioKiosk(): boolean {
  try {
    const flag = new URLSearchParams(window.location.search).get('kiosk');
    if (flag === '1') {
      window.localStorage.setItem('hub-kiosk', '1');
      // owner 27.10.2026: the kiosk iPad once opened the client app (/app) — ?kiosk=1 wins over it
      window.localStorage.removeItem('hub-client-app');
    }
    if (flag === '0') window.localStorage.removeItem('hub-kiosk');
  } catch {
    // storage blocked
  }
  // A client's phone with the 360°FACE app (PWA, /app) is never the studio kiosk.
  if (isClientApp()) return false;
  try {
    if (window.localStorage.getItem('hub-kiosk') === '1') return true;
  } catch {
    // storage blocked: fall back to the display mode
  }
  const nav = navigator as Navigator & { standalone?: boolean };
  return nav.standalone === true || window.matchMedia?.('(display-mode: standalone)').matches === true;
}

/** Locale of the phone for the phone page; Portuguese studios fall back to English. */
function phoneLocale(): Locale {
  const all: Locale[] = ['pt', 'en', 'fr', 'es', 'ru', 'uk', 'de'];
  const lang = (navigator.language || 'en').slice(0, 2).toLowerCase() as Locale;
  return all.includes(lang) ? lang : 'en';
}

/**
 * Opened from the client app /app (owner 26.10.2026): the studio is already chosen there, so the
 * flow starts at once in the app's language; the logo, «Back» from the first step and the end of the
 * flow return to the app. No «Call staff» — the client is not in the studio.
 */
export interface AppEntry {
  start: 'book' | 'gift' | 'checkin';
  locale: Locale;
  /** Booking with a membership (owner 26.10.2026). */
  membership?: BookingMembership;
}

/** Studio label for the chip at the top (the client app's names, owner 26.10.2026). */
const APP_STUDIO: Record<string, string> = { parede: 'Cascais', lisboa: 'Lisboa' };

function backToApp(pick?: AppEntry['start']) {
  window.location.assign(pick ? `/app?pick=${pick}` : '/app');
}

export function KioskV2({
  api,
  locationId,
  remote = false,
  v3 = false,
  app,
}: {
  api: HubApi;
  locationId: string;
  remote?: boolean;
  v3?: boolean;
  app?: AppEntry;
}) {
  return (
    <V3.Provider value={v3}>
      <Mem.Provider value={app?.membership}>
        <KioskInner api={api} locationId={locationId} remote={remote} v3={v3} app={app} />
      </Mem.Provider>
    </V3.Provider>
  );
}

/** The state a flow opened from the app starts in: past the welcome and home screens. */
export function appInitial(app: AppEntry): V2State {
  const s = v2Initial(undefined, app.locale);
  if (app.start === 'checkin') return v2Reducer(s, { type: 'START_REMOTE', locale: app.locale });
  const home = v2Reducer(s, { type: 'START', locale: app.locale });
  return v2Reducer(home, { type: app.start === 'book' ? 'GO_BOOK' : 'GO_GIFT' });
}

function KioskInner({
  api,
  locationId,
  remote,
  v3,
  app,
}: {
  api: HubApi;
  locationId: string;
  remote: boolean;
  v3: boolean;
  app?: AppEntry;
}) {
  const [state, raw] = useReducer(
    (s: V2State, e: V2Event) => v2Reducer(s, e),
    undefined,
    () => (app ? appInitial(app) : v2Initial()),
  );
  const dispatch = raw as Dispatch;
  const m = MESSAGES[state.locale];
  const v = V2_MESSAGES[state.locale];
  const staff = useStaff(api, locationId);
  const { welcome, gifts } = useKioskConfig(api, locationId);
  const [langOpen, setLangOpen] = useState(false);
  const [called, setCalled] = useState(false);
  /** Open video call with the operator: the screen the customer called from. */
  const [videoCall, setVideoCall] = useState<string>();
  const [contactTouched, setContactTouched] = useState(false);
  const [railEl, setRailEl] = useState<HTMLElement | null>(null);
  const [info, setInfo] = useState<{ service: Service; group?: InfoGroup }>();
  const openInfo = useCallback((service: Service, group?: InfoGroup) => setInfo({ service, group }), []);
  const reset = useCallback(() => {
    if (app) return backToApp();
    setLangOpen(false);
    setInfo(undefined);
    setCalled(false);
    setVideoCall(undefined);
    setContactTouched(false);
    dispatch({ type: 'RESET' });
  }, [dispatch, app]);
  const callStaff = useCallback(
    (screen: string) => {
      // Studio kiosk: video call with the operator (owner 20.10.2026). The phone page keeps the
      // tablet alert, the client is not in the studio yet.
      if (!remote) {
        setVideoCall(screen);
        return;
      }
      setCalled(true);
      api.callStaff(locationId, screen).catch(() => undefined);
    },
    [api, locationId, remote],
  );
  const closeVideoCall = useCallback(() => setVideoCall(undefined), []);
  useEffect(() => {
    if (!called) return;
    const t = window.setTimeout(() => setCalled(false), 10_000);
    return () => window.clearTimeout(t);
  }, [called]);

  // The phone page (owner 16.10.2026): no language screen, no inactivity reset, no card scanner.
  useEffect(() => {
    if (remote && !app) dispatch({ type: 'START_REMOTE', locale: phoneLocale() });
  }, [remote, dispatch, app]);
  // From the app: «Back» from the first step (home) or a reset (welcome) returns to the app.
  useEffect(() => {
    if (app && (state.step === 'home' || state.step === 'welcome')) backToApp();
  }, [app, state.step]);
  const phone = useIsPhone();
  const kioskKeyboard = !phone && isStudioKiosk();
  // No inactivity reset while the client talks to the operator.
  const idle = useIdle(
    !remote && !isClientApp() && videoCall === undefined && state.step !== 'welcome' && state.step !== 'done',
    reset,
  );
  useCardScanner(api, locationId, state, dispatch);
  const staffName = (id?: string) => staff.find((s) => s.id === id)?.displayName ?? '';

  if (state.step === 'welcome' && remote) return null;
  if (app && (state.step === 'welcome' || state.step === 'home')) return null;
  if (state.step === 'welcome' && v3) {
    // Owner 23.10.2026: the brand splash (pencil-drawn face line, letters fade in), languages at the end.
    return (
      <BrandSplash className="v2 v3 v2-kiosk v3-welcome" key={state.locale}>
        <div className="v2-welcome-langs">
          {welcome.map((l) => (
            <button
              key={l}
              lang={l}
              className="v2-btn v2-btn-quiet"
              onClick={() => dispatch({ type: 'START', locale: l })}
            >
              {LOCALE_NAMES[l]}
            </button>
          ))}
        </div>
      </BrandSplash>
    );
  }
  if (state.step === 'welcome') {
    return (
      <div
        className="v2 v2-kiosk v2-welcome"
        lang={state.locale}
        style={{ '--hero': `url("${BRAND.heroImage}")` } as CSSProperties}
      >
        {/* Owner 17.10.2026: brand line in English for every language, vertical on the right */}
        <span className="v2-welcome-line" aria-hidden>
          All You Need Is…
        </span>
        <div className="v2-welcome-mark">
          <span className="v2-sr">All You Need Is </span>
          360<span>°</span>FACE
        </div>
        <div className="v2-welcome-langs">
          {welcome.map((l) => (
            <button
              key={l}
              lang={l}
              className="v2-btn v2-btn-light"
              onClick={() => dispatch({ type: 'START', locale: l })}
            >
              {LOCALE_NAMES[l]}
            </button>
          ))}
        </div>
      </div>
    );
  }

  const step = bookingStep(state);

  // The kiosk keyboard only on the studio kiosk; any phone or tablet of a client uses its own.
  const Keys = remote || !kioskKeyboard ? Fragment : KeyboardProvider;

  return (
    <Keys>
      <div
        className={(v3 ? 'v2 v3 v2-kiosk' : 'v2 v2-kiosk') + (remote ? ' v2-remote' : '')}
        lang={state.locale}
        style={{ '--hero': `url("${BRAND.heroImage}")` } as CSSProperties}
      >
        <Watermark />
        <header className="v2-top">
          {/* Owner 16.10.2026: the mark as type, no grey box of logo_grey.png */}
          <button className="v2-logo v2-logo-type" onClick={reset} aria-label="360°FACE">
            360<i>°</i>FACE
          </button>
          <span className="v2-spacer" />
          {/* from the app the language is the app's (switched on its home screen) */}
          {!app && (
            <button className="v2-chip-btn" aria-haspopup="dialog" onClick={() => setLangOpen(true)}>
              <GlobeIcon />
              {LOCALE_NAMES[state.locale]}
            </button>
          )}
          {app ? (
            <button className="v2-chip-btn" onClick={() => backToApp(app.start)}>
              <span aria-hidden>📍</span> {APP_STUDIO[locationId] ?? locationId}
            </button>
          ) : (
            !remote &&
            state.step !== 'about' && (
              // owner 04.11.2026: twice as visible — red, bigger, with the phone icon
              <button
                className="v2-chip-btn v3-call"
                disabled={called || videoCall !== undefined}
                onClick={() => callStaff(state.step)}
              >
                <PhoneCallIcon /> {m.callAdmin}
              </button>
            )
          )}
        </header>

        {step !== undefined && <Steps current={step} labels={v.steps} />}

        <InfoOpen.Provider value={openInfo}>
          <RailSlot.Provider value={railEl}>
            <main className="v2-body">
              {state.notice && (
                <p className="v2-notice" role="alert">
                  {errorText(m, state.notice)}
                </p>
              )}
              <Screen
                state={state}
                dispatch={dispatch}
                api={api}
                locationId={locationId}
                staff={staff}
                staffName={staffName}
                gifts={gifts}
                m={m}
                v={v}
                callStaff={callStaff}
                called={called}
                contactTouched={contactTouched}
                onNew={reset}
                remote={remote}
              />
            </main>

            <KeyboardSlot labels={{ done: m.kbDone, space: m.kbSpace }} skipInline />

            <Rail
              state={state}
              dispatch={dispatch}
              api={api}
              locationId={locationId}
              gifts={gifts}
              m={m}
              v={v}
              onContactTry={() => setContactTouched(true)}
              slotRef={setRailEl}
            />
          </RailSlot.Provider>
        </InfoOpen.Provider>

        {info && (
          <InfoSheet
            service={info.service}
            group={info.group}
            locale={state.locale}
            m={m}
            onClose={() => setInfo(undefined)}
          />
        )}

        {called && (
          <p className="v2-toast" role="status">
            {m.callStaffDone}
          </p>
        )}

        {langOpen && (
          <div className="v2-backdrop" onClick={() => setLangOpen(false)}>
            <div
              className="v2-sheet"
              role="dialog"
              aria-modal="true"
              aria-label={m.languageTitle}
              onClick={(e) => e.stopPropagation()}
            >
              <h2>{m.languageTitle}</h2>
              <div className="v2-sheet-grid">
                {welcome.map((l) => (
                  <button
                    key={l}
                    lang={l}
                    className={l === state.locale ? 'v2-btn v2-btn-ink' : 'v2-btn v2-btn-quiet'}
                    aria-pressed={l === state.locale}
                    onClick={() => {
                      dispatch({ type: 'SET_LOCALE', locale: l });
                      setLangOpen(false);
                    }}
                  >
                    {LOCALE_NAMES[l]}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {videoCall !== undefined && (
          <AssistCall
            api={api}
            locationId={locationId}
            locale={state.locale}
            screen={videoCall}
            mirror={assistMirror(state, staffName)}
            onClose={closeVideoCall}
          />
        )}

        {idle.warning !== undefined && (
          <div className="v2-backdrop">
            <div className="v2-sheet v2-idle" role="alertdialog" aria-modal="true" aria-label={v.idleTitle}>
              <h2>{v.idleTitle}</h2>
              <p>{v.idleSub(idle.warning)}</p>
              <div className="v2-sheet-grid">
                <button className="v2-btn v2-btn-red" onClick={idle.stay}>
                  {v.idleStay}
                </button>
                <button className="v2-btn v2-btn-quiet" onClick={reset}>
                  {v.idleLeave}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Keys>
  );
}

/** Five named steps; the current one is spelled out, done ones are filled. */
function Steps({ current, labels }: { current: number; labels: string[] }) {
  return (
    <ol className="v2-steps" aria-label={`${current}/${labels.length}`}>
      {labels.map((l, i) => (
        <li
          key={l}
          className={i + 1 < current ? 'done' : i + 1 === current ? 'now' : ''}
          aria-current={i + 1 === current ? 'step' : undefined}
        >
          <em aria-hidden>{i + 1 < current ? '✓' : i + 1}</em>
          <span>{l}</span>
        </li>
      ))}
    </ol>
  );
}

/** Where the screen's main action goes: the right half of the bottom panel. */
const RailSlot = createContext<HTMLElement | null>(null);

/**
 * Puts a screen's main action ("Continue", "Find my booking", …) into the bottom panel, so it is
 * always in the same place and never needs scrolling (owner 17.10.2026). The screen keeps its state.
 */
function RailAction({ children }: { children: ReactNode }) {
  const el = useContext(RailSlot);
  return el ? createPortal(children, el) : null;
}

/** Screens without the bottom panel. */
const NO_RAIL: V2Step[] = ['welcome', 'home', 'about', 'done'];

/**
 * The bottom panel of every kiosk screen (owner 17.10.2026): two large buttons of the same size —
 * "Back" on the left, the main action ("Continue", red) on the right. In the booking steps the
 * chosen treatment, time and total sit above them.
 */
function Rail({
  state,
  dispatch,
  api,
  locationId,
  gifts,
  m,
  v,
  onContactTry,
  slotRef,
}: {
  state: V2State;
  dispatch: Dispatch;
  api: HubApi;
  locationId: string;
  gifts: BookingGiftOption[];
  m: Messages;
  v: V2Messages;
  onContactTry: () => void;
  slotRef: (el: HTMLDivElement | null) => void;
}) {
  const [services, setServices] = useState<Service[]>([]);
  const [products, setProducts] = useState<ProductOffer[]>([]);
  useEffect(() => {
    api.services(locationId).then(setServices, () => undefined);
    api.products(locationId).then(setProducts, () => undefined);
  }, [api, locationId]);
  const mem = useContext(Mem);
  const v3 = useV3();
  if (NO_RAIL.includes(state.step)) return null;
  const inBooking = state.mode === 'booking' && BOOKING_STEPS.includes(state.step);
  const back = canGoBack(state) ? (
    <button className="v2-btn v2-rail-back" onClick={() => dispatch({ type: 'BACK' })}>
      {m.back}
    </button>
  ) : null;

  const { locale } = state;
  const s = inBooking ? state.service : undefined;
  const addons = state.addonIds.map((id) => services.find((x) => x.id === id)).filter((x): x is Service => Boolean(x));
  const productsMinor = state.productIds.reduce(
    (t, id) => t + (products.find((p) => p.id === id)?.finalPrice.amountMinor ?? 0),
    0,
  );
  // a treatment covered by the membership is one session, not money (owner 26.10.2026)
  const mainMinor = s && !coveredBy(mem, s) ? s.price.amountMinor : 0;
  const totalMinor = mainMinor + addons.reduce((t, a) => t + a.price.amountMinor, 0) + productsMinor;
  const minutes = (s?.durationMin ?? 0) + addons.reduce((t, a) => t + a.durationMin, 0);

  let action: ReactNode = null;
  if (inBooking && state.step === 'service')
    action = (
      <button className="v2-btn v2-btn-red" disabled={!s} onClick={() => dispatch({ type: 'SERVICE_DONE' })}>
        {m.continue}
      </button>
    );
  if (inBooking && state.step === 'time')
    action = (
      <button
        className="v2-btn v2-btn-red"
        disabled={!state.slot}
        onClick={() => state.slot && dispatch({ type: 'PICK_SLOT', slot: state.slot })}
      >
        {m.continue}
      </button>
    );
  // v3: the extras screen puts its own two-line bar here (RailAction), owner 31.10.2026
  if (inBooking && state.step === 'extras' && !v3)
    action = (
      <button className="v2-btn v2-btn-red" onClick={() => dispatch({ type: 'EXTRAS_DONE' })}>
        {state.addonIds.length > 0 || state.productIds.length > 0 || state.bookingGiftId ? m.continue : v.noExtras}
      </button>
    );
  if (inBooking && state.step === 'contact')
    action = (
      <button
        className="v2-btn v2-btn-red"
        onClick={() => {
          onContactTry();
          dispatch({ type: 'CONTACT_DONE' });
          if (contactErrors(state).length > 0) showContactProblem();
        }}
      >
        {m.continue}
      </button>
    );
  if (inBooking && state.step === 'confirm' && s && state.slot) {
    const base = mainMinor + addons.reduce((t, a) => t + a.price.amountMinor, 0);
    // a gift chosen earlier counts only while the amount still unlocks it
    const giftId = eligibleGifts(gifts, base).some((g) => g.id === state.bookingGiftId)
      ? state.bookingGiftId
      : undefined;
    const phone = toE164(state.customer.phoneCountry, state.customer.phone)!;
    const known = knownYes(state) ? state.known!.view : undefined;
    action = (
      <SubmitButton
        className="v2-btn v2-btn-red"
        label={v.confirmBook}
        busyLabel={m.sending}
        m={m}
        run={async () => {
          try {
            const visit = await api.createVisit(locationId, {
              idempotencyKey: state.idempotencyKey,
              serviceIds: [s.id],
              addonIds: state.addonIds,
              productIds: state.productIds,
              giftId,
              staffId: state.slot!.staffId,
              start: state.slot!.start,
              // a recognised client: the server fills in the name and e-mail it already has
              customer: known
                ? {
                    recognized: known.token!,
                    phone,
                    ...(known.emailMask ? {} : { email: state.customer.email.trim() }),
                  }
                : {
                    fullName: state.customer.fullName.trim(),
                    phone,
                    email: state.customer.email.trim(),
                  },
              consents: { privacy: true, marketing: state.customer.marketing },
              ...(bookingComment(mem, state.comment) ? { comment: bookingComment(mem, state.comment) } : {}),
            });
            dispatch({ type: 'BOOKED', visit, today: kioskToday() });
          } catch (e) {
            if (e instanceof ApiError && e.code === 'SLOT_TAKEN') return dispatch({ type: 'SLOT_TAKEN' });
            if (e instanceof ApiError && e.code === 'RECOGNITION_EXPIRED')
              return dispatch({ type: 'RECOGNITION_EXPIRED' });
            throw e;
          }
        }}
      />
    );
  }

  return (
    <footer className={inBooking ? 'v2-rail' : 'v2-rail v2-rail-plain'}>
      {inBooking && (
        <div className="v2-rail-info">
          <div className="v2-rail-sum" aria-live="polite">
            {s ? (
              <>
                <b>
                  {localize(s.title, locale)}
                  {addons.length > 0 && <small> + {addons.map((a) => localize(a.title, locale)).join(', ')}</small>}
                </b>
                <span>
                  {minutes} {m.minutes}
                  {state.slot &&
                    ` · ${formatDay(state.slot.start.slice(0, 10), locale)} ${state.slot.start.slice(11, 16)}–${endTime(state.slot.start, minutes)}`}
                </span>
              </>
            ) : (
              <span>{v.pickDuration}</span>
            )}
          </div>
          {s && (
            <strong className="v2-rail-total">
              {formatMoney({ amountMinor: totalMinor, currency: s.price.currency }, locale)}
            </strong>
          )}
        </div>
      )}
      <div className="v2-rail-row">
        <div className="v2-rail-left">{back}</div>
        <div className="v2-rail-action">
          {action}
          {/* screens put their main action here (RailAction); kept apart from React's own children */}
          <div className="v2-rail-portal" ref={slotRef} />
        </div>
      </div>
    </footer>
  );
}

interface ScreenProps {
  state: V2State;
  dispatch: Dispatch;
  api: HubApi;
  locationId: string;
  staff: Staff[];
  staffName: (id?: string) => string;
  gifts: BookingGiftOption[];
  m: Messages;
  v: V2Messages;
  callStaff: (screen: string) => void;
  called: boolean;
  contactTouched: boolean;
  onNew: () => void;
  /** The phone page before the visit (owner 16.10.2026). */
  remote: boolean;
}

/** Text with "360°FACE" written as the mark (red degree sign), e.g. "I have a booking at 360°FACE". */
function Branded({ text }: { text: string }) {
  const i = text.indexOf('360°FACE');
  if (i < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, i)}
      <span className="v2-brand">
        360<i>°</i>FACE
      </span>
      {text.slice(i + '360°FACE'.length)}
    </>
  );
}

/** "126 reviews" under the therapist's rating (Altegio review count, owner 23.10.2026). */
const REVIEWS: Record<Locale, (n: number) => string> = {
  pt: (n) => `${n} avaliações`,
  en: (n) => `${n} reviews`,
  fr: (n) => `${n} avis`,
  es: (n) => `${n} reseñas`,
  ru: (n) => `отзывов: ${n}`,
  uk: (n) => `відгуків: ${n}`,
  de: (n) => `${n} Bewertungen`,
};

const MEMBERSHIP_TEXT: Record<
  Locale,
  { title: string; left: (n: number) => string; unlimited: string; until: (d: string) => string; frozen: string }
> = {
  pt: {
    title: 'O seu abonamento',
    left: (n) => (n === 1 ? 'Resta 1 sessão' : `Restam ${n} sessões`),
    unlimited: 'Sessões ilimitadas',
    until: (d) => `válido até ${d}`,
    frozen: 'congelado',
  },
  en: {
    title: 'Your membership',
    left: (n) => (n === 1 ? '1 session left' : `${n} sessions left`),
    unlimited: 'Unlimited sessions',
    until: (d) => `valid until ${d}`,
    frozen: 'paused',
  },
  fr: {
    title: 'Votre abonnement',
    left: (n) => (n === 1 ? 'Il reste 1 séance' : `Il reste ${n} séances`),
    unlimited: 'Séances illimitées',
    until: (d) => `valable jusqu’au ${d}`,
    frozen: 'suspendu',
  },
  es: {
    title: 'Su bono',
    left: (n) => (n === 1 ? 'Queda 1 sesión' : `Quedan ${n} sesiones`),
    unlimited: 'Sesiones ilimitadas',
    until: (d) => `válido hasta ${d}`,
    frozen: 'congelado',
  },
  ru: {
    title: 'Ваш абонемент',
    left: (n) => `Осталось сеансов: ${n}`,
    unlimited: 'Без ограничения сеансов',
    until: (d) => `действует до ${d}`,
    frozen: 'заморожен',
  },
  uk: {
    title: 'Ваш абонемент',
    left: (n) => `Залишилось сеансів: ${n}`,
    unlimited: 'Без обмеження сеансів',
    until: (d) => `діє до ${d}`,
    frozen: 'заморожено',
  },
  de: {
    title: 'Ihr Abo',
    left: (n) => (n === 1 ? 'Noch 1 Sitzung' : `Noch ${n} Sitzungen`),
    unlimited: 'Unbegrenzte Sitzungen',
    until: (d) => `gültig bis ${d}`,
    frozen: 'pausiert',
  },
};

/**
 * Kiosk v3 (owner 23.10.2026): after "Is this you?" the client sees her membership from Altegio —
 * name, sessions left, valid until. Read-only; nothing shows while loading, without one or on error.
 */
function MembershipBanner({
  api,
  locationId,
  who,
  locale,
}: {
  api: HubApi;
  locationId: string;
  who: { phone: string } | { cardCode: string };
  locale: Locale;
}) {
  const [list, setList] = useState<ClientMembership[]>([]);
  const key = JSON.stringify(who);
  useEffect(() => {
    let live = true;
    api.checkInMemberships?.(locationId, who).then(
      (x) => live && setList(x),
      () => undefined,
    );
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, locationId, key]);
  if (list.length === 0) return null;
  const t = MEMBERSHIP_TEXT[locale];
  return (
    <div className="v3-members">
      {list.map((x, i) => {
        const left =
          x.visitsLeft !== undefined
            ? x.visitsLeft
            : x.services.reduce((n, s) => n + (s.visitsLeft ?? 0), 0) || undefined;
        return (
          <div key={i} className="v3-member">
            <span className="v3-member-ico" aria-hidden>
              ✦
            </span>
            <span className="v3-member-text">
              <small>{t.title}</small>
              <b>{x.title}</b>
              <span>
                {x.unlimited ? t.unlimited : left !== undefined ? t.left(left) : ''}
                {x.expiresAt ? ` · ${t.until(formatDay(x.expiresAt.slice(0, 10), locale))}` : ''}
                {x.frozen ? ` · ${t.frozen}` : ''}
              </span>
              {x.services.length > 0 && (
                <em className="v3-member-for">{x.services.map((sv) => sv.title).join(' · ')}</em>
              )}
            </span>
            {left !== undefined && !x.unlimited && <strong className="v3-member-count">{left}</strong>}
          </div>
        );
      })}
    </div>
  );
}

/** v3: a title block with a small 3D picture on the right; plain in v2. */
function V3Head({ art, children }: { art: string; children: ReactNode }) {
  const v3 = useV3();
  if (!v3) return <>{children}</>;
  return (
    <div className="v3-head">
      <div>{children}</div>
      <img src={art} alt="" />
    </div>
  );
}

/** The 360°FACE mark, large and soft behind the content (owner 16.10.2026). Decorative only. */
function Watermark() {
  return (
    <span className="v2-watermark" aria-hidden>
      360<i>°</i>FACE
    </span>
  );
}

/** Pictures of the «Saber mais» page (the kiosk's own 3D set). */
const ABOUT_ART = {
  hero: '/v3/s-lifting.jpg',
  face: '/v3/s-korugi.jpg',
  body: '/v3/s-body.jpg',
  skin: '/v3/s-cleansing.jpg',
};

/**
 * «Saber mais sobre nós» (owner 03.11.2026): one page for a visitor from the street — what we do,
 * for whom, from how much — and «Marcar agora» straight into the booking. Prices are live: the
 * cheapest treatment of each kind in the catalog.
 */
function AboutScreen({
  services,
  dispatch,
  m,
  v,
  locale,
  callStaff,
  called,
}: {
  services: Service[];
  dispatch: Dispatch;
  m: Messages;
  v: V2Messages;
  locale: Locale;
  callStaff: () => void;
  called: boolean;
}) {
  // «from …»: the cheapest full treatment of the kind (a 20-min buccal is not a first visit)
  const cheapest = (kind: Service['kind']) =>
    services
      .filter((s) => s.kind === kind && s.active && s.durationMin >= 30)
      .sort((a, b) => a.price.amountMinor - b.price.amountMinor || a.durationMin - b.durationMin)[0];
  const from = (s?: Service) =>
    s ? `${v.aboutFrom} ${formatMoney(s.price, locale)} · ${s.durationMin} ${m.minutes}` : '';
  // the first-visit offer: Express to Impress when the catalog has it (owner 03.11.2026)
  const face =
    services.find((s) => s.kind === 'face' && s.active && /express/i.test(localize(s.title, 'en'))) ?? cheapest('face');
  const body = cheapest('body');
  const skin = cheapest('skin');
  const durations = services
    .filter((s) => s.kind === 'face' && s.active && s.durationMin >= 30)
    .map((s) => s.durationMin);
  const minutes = durations.length ? `${Math.min(...durations)}–${Math.max(...durations)}` : '30–65';
  const studios = STUDIOS.filter((x) => !x.hidden).length;
  const cards: Array<[string, string, string, Service | undefined]> = [
    [v.aboutFace, v.aboutFaceText, ABOUT_ART.face, face],
    [v.aboutBody, v.aboutBodyText, ABOUT_ART.body, body],
    [v.aboutSkin, v.aboutSkinText, ABOUT_ART.skin, skin],
  ];
  return (
    <div className="v3-about">
      {/* owner 04.11.2026: who we are first, big — then what we do */}
      <section className="v3-about-who">
        <h1>{v.aboutWho}</h1>
        <p>{v.aboutWhoText}</p>
        <p>{v.aboutWhoText2}</p>
      </section>
      <h2 className="v2-h1 v3-about-h">{v.aboutTitle}</h2>
      <p className="v2-lead">{v.aboutLead}</p>
      <div className="v3-about-hero">
        <img src={ABOUT_ART.hero} alt="" />
        <span className="v3-about-hero-fade" aria-hidden />
        <div>
          <b>{v.aboutHero}</b>
          <em>{v.aboutHeroSub}</em>
        </div>
      </div>
      <section className="v3-about-solves">
        <h2>{v.aboutSolves}</h2>
        <ul>
          {v.aboutSolvesList.map((x) => (
            <li key={x}>{x}</li>
          ))}
        </ul>
      </section>
      <div className="v3-about-grid">
        {cards.map(([title, text, img, s]) => (
          <div className="v3-about-card" key={title}>
            <img src={img} alt="" />
            <span className="v3-about-card-fade" aria-hidden />
            <div>
              <b>{title}</b>
              <p>{text}</p>
              {s && <u>{from(s)}</u>}
            </div>
          </div>
        ))}
        <div className="v3-about-card v3-about-first">
          <div>
            <b>{v.aboutFirst}</b>
            <p>{v.aboutFirstText}</p>
            {face && (
              <u>
                {v.aboutTry} «{localize(face.title, locale)}» · {face.durationMin} {m.minutes} ·{' '}
                {formatMoney(face.price, locale)}
              </u>
            )}
          </div>
        </div>
      </div>
      <div className="v3-about-facts">
        <div>
          <b>{studios}</b>
          <span>{v.aboutStudios}</span>
        </div>
        <div>
          <b>{minutes}</b>
          <span>{v.aboutMinutes}</span>
        </div>
        <div>
          <b>0</b>
          <span>{v.aboutZero}</span>
        </div>
      </div>
      {/* owner 04.11.2026: questions → the team, right where the reading ends */}
      <section className="v3-about-ask">
        <b>{v.aboutQuestions}</b>
        <button className="v2-chip-btn v3-call" disabled={called} onClick={callStaff}>
          <PhoneCallIcon /> {called ? m.callStaffDone : m.callAdmin}
        </button>
      </section>
      <div className="v3-about-rail">
        <button className="v2-btn v2-rail-back" onClick={() => dispatch({ type: 'BACK' })}>
          {m.back}
        </button>
        <button className="v2-btn v2-btn-red" onClick={() => dispatch({ type: 'GO_BOOK' })}>
          {v.aboutBook}
        </button>
      </div>
    </div>
  );
}

function Screen(p: ScreenProps) {
  const { state, dispatch, api, locationId, m, v } = p;
  const v3 = useV3();
  switch (state.step) {
    case 'welcome':
      return null;
    case 'home':
      return (
        <div className="v2-home">
          <h1 className="v2-h1">{m.menuTitle}</h1>
          <button className="v2-door v2-door-main" onClick={() => dispatch({ type: 'GO_CHECKIN' })}>
            <span className="v2-door-line" aria-hidden>
              All You Need Is…
            </span>
            {v3 ? (
              <img className="v3-door-art" src={V3_ART.checkin} alt="" />
            ) : (
              <span className="v2-door-ico" aria-hidden>
                <CheckIcon size={40} />
              </span>
            )}
            <b>
              <Branded text={v.homeCheckin} />
            </b>
            <span>{v.homeCheckinSub}</span>
          </button>
          <div className="v2-home-pair">
            <button className="v2-door" onClick={() => dispatch({ type: 'GO_BOOK' })}>
              {v3 ? (
                <img className="v3-door-art" src={V3_ART.book} alt="" />
              ) : (
                <span className="v2-door-ico small" aria-hidden>
                  <CalendarIcon />
                </span>
              )}
              <b>{v.homeBook}</b>
              <span>{v.homeBookSub}</span>
            </button>
            <button className="v2-door" onClick={() => dispatch({ type: 'GO_GIFT' })}>
              {v3 ? (
                <img className="v3-door-art" src={V3_ART.gift} alt="" />
              ) : (
                <span className="v2-door-ico small" aria-hidden>
                  <GiftIcon />
                </span>
              )}
              <b>{v.homeGift}</b>
              <span>{v.homeGiftSub}</span>
            </button>
          </div>
          {v3 && (
            // Owner 03.11.2026: a visitor from the street reads about us instead of asking the therapist
            <button className="v2-door v3-door-about" onClick={() => dispatch({ type: 'GO_ABOUT' })}>
              <img className="v3-banner-art" src={ABOUT_ART.hero} alt="" />
              <span className="v3-banner-fade" aria-hidden />
              <span className="v3-door-about-text">
                <b>✦ {v.homeAbout}</b>
                <span>{v.homeAboutSub}</span>
              </span>
            </button>
          )}
        </div>
      );

    case 'about':
      return (
        <Load load={() => api.services(locationId)} m={m} deps={[locationId, 'services']}>
          {(services) => (
            <AboutScreen
              services={services}
              dispatch={dispatch}
              m={m}
              v={v}
              locale={state.locale}
              callStaff={() => p.callStaff('about')}
              called={p.called}
            />
          )}
        </Load>
      );

    case 'service':
      return (
        <Load load={() => api.services(locationId)} m={m} deps={[locationId, 'services']}>
          {(services) => <ServiceScreen services={services} state={state} dispatch={dispatch} m={m} v={v} />}
        </Load>
      );

    case 'time':
      return <TimeScreen {...p} />;

    case 'extras':
      return <ExtrasScreen {...p} />;

    case 'contact':
      return <ContactScreen {...p} />;

    case 'confirm':
      return <ConfirmScreen {...p} />;

    case 'find':
      return <FindScreen {...p} />;

    case 'hello': {
      const first = state.matches[0];
      return (
        <div className="v2-stack">
          <h1 className="v2-h1">{v.helloTitle(first?.firstName ?? '')}</h1>
          <p className="v2-lead">{v.helloSub}</p>
          {v3 && !p.remote && (
            <MembershipBanner
              api={api}
              locationId={locationId}
              who={state.card ? { cardCode: state.card } : { phone: toE164(state.country, state.phone)! }}
              locale={state.locale}
            />
          )}
          {state.matches.map((x) => (
            <div className="v2-card v2-booking" key={x.ref}>
              <span className="v2-big-time">{x.start.slice(11, 16)}</span>
              <div>
                <b>{x.titles.join(' + ')}</b>
                <span>
                  {m.with} {p.staffName(x.staffId) || '—'}
                </span>
              </div>
              <SubmitButton
                className="v2-btn v2-btn-red v2-wide"
                label={p.remote ? v.itsMe : v.imHere}
                busyLabel={m.sending}
                m={m}
                run={async () => {
                  if (p.remote) {
                    const phone = toE164(state.country, state.phone)!;
                    dispatch({ type: 'CHECKED_IN', visit: await api.prepare(locationId, x.ref, phone) });
                    return;
                  }
                  const visit = await api.checkIn(
                    locationId,
                    x.ref,
                    state.card ? { cardCode: state.card } : { phone: toE164(state.country, state.phone)! },
                  );
                  dispatch({ type: 'CHECKED_IN', visit });
                }}
              />
            </div>
          ))}
          <button className="v2-btn v2-btn-quiet" onClick={() => dispatch({ type: 'BACK' })}>
            {v.notMe}
          </button>
        </div>
      );
    }

    case 'cp':
      return (
        <Load load={() => api.classPassToday(locationId)} m={m} deps={[locationId, 'cp']}>
          {(times) => (
            <div className="v2-stack">
              <h1 className="v2-h1">{m.cpTitle}</h1>
              {times.length === 0 && <p className="v2-lead">{m.cpNone}</p>}
              <div className="v2-slots">
                {times.map((x) => (
                  <SubmitButton
                    key={x.ref}
                    className="v2-slot"
                    m={m}
                    label={x.start.slice(11, 16)}
                    busyLabel="…"
                    run={async () => {
                      const { fullName } = await api.classPassIdentify(locationId, x.ref);
                      dispatch({ type: 'CP_PICK', ref: x.ref, start: x.start, fullName });
                    }}
                  />
                ))}
              </div>
              <div className="v2-card">
                <b>{m.cpNotFound}</b>
                <p>{m.cpChat}</p>
                <button
                  className="v2-btn v2-btn-quiet"
                  disabled={p.called}
                  onClick={() => p.callStaff('classpass_not_found')}
                >
                  {m.callAdmin}
                </button>
              </div>
            </div>
          )}
        </Load>
      );

    case 'cp_confirm':
      return (
        <div className="v2-stack v2-center">
          <span className="v2-big-time">{state.classPass!.start.slice(11, 16)}</span>
          <h1 className="v2-h1">{m.cpIsYou(state.classPass!.fullName)}</h1>
          <div className="v2-pair">
            <SubmitButton
              className="v2-btn v2-btn-red"
              label={m.cpYes}
              busyLabel={m.sending}
              m={m}
              run={async () =>
                dispatch({ type: 'CP_CHECKED_IN', visit: await api.classPassCheckIn(locationId, state.classPass!.ref) })
              }
            />
            <button className="v2-btn v2-btn-quiet" onClick={() => dispatch({ type: 'BACK' })}>
              {m.cpNo}
            </button>
          </div>
        </div>
      );

    case 'offer':
      return <OfferScreen {...p} />;

    case 'gift':
      return <GiftScreen {...p} />;

    case 'gift_phone':
      return <GiftPhoneScreen {...p} />;

    case 'gift_card':
      return <GiftCardScreen {...p} />;

    case 'pay':
      return (
        <div className="v2-stack">
          <h1 className="v2-h1">{state.mode === 'gift' ? m.paymentTitle : v.payTitle}</h1>
          <p className="v2-lead">{state.mode === 'gift' ? m.paymentSub : v.paySub}</p>
          <div className="v2-pay-grid">
            {KIOSK_PAY_ORDER.map((method: PaymentMethod) => (
              <SubmitButton
                key={method}
                className="v2-pay"
                m={m}
                label={
                  <>
                    {v3 && PAY_ART[method] ? (
                      <img className="v3-pay-art" src={PAY_ART[method]} alt="" />
                    ) : (
                      <span aria-hidden>{PAY_ICON[method]}</span>
                    )}
                    <b>{m.methods[method]}</b>
                    {method === 'qr_transfer' && <small className="v3-pay-sub">Wise · bunq · Revolut</small>}
                  </>
                }
                busyLabel={m.sending}
                run={async () => dispatch({ type: 'PAID', visit: await api.selectPaymentMethod(state.visit!, method) })}
              />
            ))}
          </div>
          <RailAction>
            <button className="v2-btn v2-btn-light" onClick={() => dispatch({ type: 'PAID' })}>
              {v.payLater}
            </button>
          </RailAction>
        </div>
      );

    case 'questionnaire':
      return <QuestionnaireScreen {...p} />;

    case 'done':
      return <DoneScreen {...p} />;
  }
}

/** Owner 24.10.2026: order of the payment tiles on the kiosk. */
const KIOSK_PAY_ORDER: PaymentMethod[] = [
  'cash',
  'mbway',
  'qr_transfer',
  'bank_transfer',
  'gift_card',
  'card_terminal',
];
// every method the domain knows is shown exactly once
if (KIOSK_PAY_ORDER.length !== PAYMENT_METHODS.length || !PAYMENT_METHODS.every((x) => KIOSK_PAY_ORDER.includes(x)))
  throw new Error('KIOSK_PAY_ORDER is out of date');

function ServiceScreen({
  services,
  state,
  dispatch,
  m,
  v,
}: {
  services: Service[];
  state: V2State;
  dispatch: Dispatch;
  m: Messages;
  v: V2Messages;
}) {
  const { locale } = state;
  const mem = useContext(Mem);
  const cards = serviceCards(services);
  const kinds = KIND_ORDER.filter((k) => cards.some((c) => c.kind === k));
  const [kind, setKind] = useState<ServiceKind>(() => state.service?.kind ?? kinds[0] ?? 'face');
  // Owner 26.10.2026: with a membership its treatments come first; «show all» opens the rest.
  const covered = cards.filter((c) => c.variants.some((x) => coveredBy(mem, x)));
  const [showAll, setShowAll] = useState(() => Boolean(state.service && !coveredBy(mem, state.service)));
  if (mem && covered.length > 0) {
    const rest = cards.filter((c) => !covered.includes(c));
    const restKinds = KIND_ORDER.filter((k) => rest.some((c) => c.kind === k));
    const restKind = restKinds.includes(kind) ? kind : (restKinds[0] ?? kind);
    return (
      <div className="v2-stack">
        <h1 className="v2-h1">{m.chooseService}</h1>
        <p className="v2-sec">{v.memIncluded}</p>
        <div className="v2-cards">
          {covered.map((c) => (
            <ServiceCardView key={c.id} card={c} state={state} dispatch={dispatch} m={m} v={v} locale={locale} />
          ))}
        </div>
        <button
          type="button"
          className="v2-switch-row"
          role="switch"
          aria-checked={showAll}
          onClick={() => setShowAll((x) => !x)}
        >
          <span>{v.memShowAll}</span>
          <i className={showAll ? 'v2-switch on' : 'v2-switch'} aria-hidden />
        </button>
        {showAll && rest.length > 0 && (
          <>
            <p className="v2-sec">{v.memOther}</p>
            <div className="v2-tabs" role="tablist">
              {restKinds.map((k) => (
                <button key={k} role="tab" aria-selected={k === restKind} onClick={() => setKind(k)}>
                  {m.kinds[k]}
                </button>
              ))}
            </div>
            <div className="v2-cards">
              {rest
                .filter((c) => c.kind === restKind)
                .map((c) => (
                  <ServiceCardView key={c.id} card={c} state={state} dispatch={dispatch} m={m} v={v} locale={locale} />
                ))}
            </div>
          </>
        )}
      </div>
    );
  }
  return (
    <div className="v2-stack">
      <h1 className="v2-h1">{m.chooseService}</h1>
      <div className="v2-tabs" role="tablist">
        {kinds.map((k) => (
          <button key={k} role="tab" aria-selected={k === kind} onClick={() => setKind(k)}>
            {m.kinds[k]}
          </button>
        ))}
      </div>
      <div className="v2-cards">
        {cards
          .filter((c) => c.kind === kind)
          .map((c) => (
            <ServiceCardView key={c.id} card={c} state={state} dispatch={dispatch} m={m} v={v} locale={locale} />
          ))}
      </div>
    </div>
  );
}

function ServiceCardView({
  card,
  state,
  dispatch,
  m,
  v,
  locale,
}: {
  card: ServiceCard;
  state: V2State;
  dispatch: Dispatch;
  m: Messages;
  v: V2Messages;
  locale: Locale;
}) {
  const first = card.variants[0]!;
  const chosen = card.variants.find((x) => x.id === state.service?.id);
  const shown = chosen ?? first;
  const v3 = useV3();
  const img = (v3 && serviceArt(card.title ?? first.title)) || card.variants.find((x) => x.imageUrl)?.imageUrl;
  const single = card.variants.length === 1;
  const mem = useContext(Mem);
  const price = (s: Service) => (coveredBy(mem, s) ? v.memSession : formatMoney(s.price, locale));
  const pick = (s: Service) => dispatch({ type: 'PICK_SERVICE', cardId: card.id, service: s });
  const info = v3 ? (
    <InfoButton
      service={shown}
      group={single ? undefined : { title: card.title, variants: card.variants }}
      locale={locale}
    />
  ) : null;
  // v3 (owner 2.11.2026): the same banner as the extras — picture bleeds to the right edge, the card
  // colour fades over it, a chosen card turns dark; «Learn more» sits inside the text block.
  const body = v3 ? (
    <>
      {img && <img className="v3-banner-art" src={img} alt="" />}
      <span className="v3-banner-fade" aria-hidden />
      <span className="v2-svc-text">
        <b>{card.title ? localize(card.title, locale) : localize(first.title, locale)}</b>
        {shown.description && <span className="v2-svc-desc">{localize(shown.description, locale)}</span>}
        {single && (
          <span className="v2-svc-meta">
            {chosen ? `${v.selected} · ` : ''}
            {first.durationMin} {m.minutes} · {price(first)}
          </span>
        )}
        <span className="v3-info-inline">{info}</span>
      </span>
    </>
  ) : (
    <>
      {img && <span className="v2-svc-img" style={{ backgroundImage: `url("${img}")` }} aria-hidden />}
      <span className="v2-svc-text">
        <b>{card.title ? localize(card.title, locale) : localize(first.title, locale)}</b>
        {shown.description && <span className="v2-svc-desc">{localize(shown.description, locale)}</span>}
        {single && (
          <span className="v2-svc-meta">
            {first.durationMin} {m.minutes} · {price(first)}
          </span>
        )}
      </span>
    </>
  );
  if (single)
    return v3 ? (
      <button
        className={chosen ? 'v2-svc v3-banner on' : 'v2-svc v3-banner'}
        aria-pressed={Boolean(chosen)}
        onClick={() => pick(first)}
      >
        {body}
      </button>
    ) : (
      <button className={chosen ? 'v2-svc on' : 'v2-svc'} aria-pressed={Boolean(chosen)} onClick={() => pick(first)}>
        {body}
      </button>
    );
  return (
    <div className={[chosen ? 'v2-svc on' : 'v2-svc', v3 ? 'v3-banner v3-banner-group' : ''].join(' ').trim()}>
      {!v3 && info}
      {/* owner 03.11.2026: the picture fills only the header, not the whole card (on a phone the
          durations stack up and the picture showed just the top of the head) */}
      {v3 ? <div className="v3-banner-head">{body}</div> : body}
      <div className="v2-variants" role="radiogroup" aria-label={v.pickDuration}>
        {card.variants.map((s) => (
          <button key={s.id} role="radio" aria-checked={s.id === chosen?.id} onClick={() => pick(s)}>
            <b>
              {s.durationMin} {m.minutes}
            </b>
            <span>{price(s)}</span>
            <small>{localize(s.title, locale)}</small>
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * The therapist's photo from Altegio (idea I-1, owner 17.10.2026), or her initials when there is
 * no photo or it does not load.
 */
function Avatar({ person, size = 40 }: { person?: Staff; size?: number }) {
  const [broken, setBroken] = useState(false);
  if (!person) return null;
  const initials = person.displayName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('');
  return (
    <span className="v2-avatar" style={{ width: size, height: size }} aria-hidden>
      {person.photoUrl && !broken ? (
        <img src={person.photoUrl} alt="" onError={() => setBroken(true)} />
      ) : (
        <span>{initials}</span>
      )}
    </span>
  );
}

/**
 * Owner 17.10.2026: under each date, who has free time that day. Free dates are read per
 * therapist (one request each, the API throttles towards Altegio); nothing shows until loaded.
 */
function useStaffDays(api: HubApi, locationId: string, serviceId: string, performers: Staff[]) {
  const [days, setDays] = useState<Record<string, string[]>>({});
  const ids = performers.map((p) => p.id).join(',');
  useEffect(() => {
    let live = true;
    setDays({});
    if (performers.length < 2) return;
    Promise.all(
      performers.map((p) =>
        api.dates(locationId, [serviceId], p.id).then(
          (ds) => ({ id: p.id, ds }),
          () => ({ id: p.id, ds: [] as string[] }),
        ),
      ),
    ).then((rows) => {
      if (!live) return;
      setDays(staffByDay(rows));
    });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, locationId, serviceId, ids]);
  return days;
}

function TimeScreen({ state, dispatch, api, locationId, staff, staffName, m, v }: ScreenProps) {
  const v3 = useV3();
  const s = state.service!;
  const { locale } = state;
  const performers = staff.filter((x) => s.staffIds.length === 0 || s.staffIds.includes(x.id));
  const staffDays = useStaffDays(api, locationId, s.id, performers);
  return (
    <Load load={() => api.dates(locationId, [s.id])} m={m} deps={[locationId, s.id, 'dates']}>
      {(dates) => {
        // Owner 27.10.2026: every day is shown; the days without times for this treatment (or for
        // the chosen therapist) are dimmed and cannot be tapped.
        const open = bookableDays(dates, state.staffFilter, staffDays);
        const date = state.date && open.includes(state.date) ? state.date : open[0];
        return (
          <div className="v2-stack">
            <h1 className="v2-h1">{m.chooseTime}</h1>
            <div className="v2-days" role="listbox" aria-label={m.chooseDate}>
              {dayStrip(kioskToday(), 21).map((d) => (
                <button
                  key={d}
                  role="option"
                  aria-selected={d === date}
                  aria-disabled={!open.includes(d)}
                  disabled={!open.includes(d)}
                  onClick={() => dispatch({ type: 'PICK_DATE', date: d })}
                >
                  <small>{weekday(d, locale)}</small>
                  <b>{Number(d.slice(8, 10))}</b>
                  <small>{month(d, locale)}</small>
                  {staffDays[d] && (
                    <em className="v2-day-staff">{dayStaffLabel(staffDays[d].map((id) => staffName(id)))}</em>
                  )}
                </button>
              ))}
            </div>
            {/* owner 17.10.2026: the therapist with a large photo (as in the confirmation e-mail), under the dates */}
            {/* v3: the therapist's photo also when only one does this treatment (owner 25.10.2026) */}
            {(performers.length > 1 || (v3 && performers.length === 1)) && (
              <div className="v2-team" role="radiogroup" aria-label={v.anyTherapist}>
                {!v3 && (
                  <button
                    className="v2-person any"
                    role="radio"
                    aria-checked={!state.staffFilter}
                    onClick={() => dispatch({ type: 'FILTER_STAFF' })}
                  >
                    <span className="v2-person-any" aria-hidden>
                      {performers.slice(0, 2).map((x) => (
                        <Avatar key={x.id} person={x} size={44} />
                      ))}
                    </span>
                    <b>{v.anyTherapist}</b>
                  </button>
                )}
                {performers.map((x) => (
                  <button
                    key={x.id}
                    className="v2-person"
                    role="radio"
                    aria-checked={state.staffFilter === x.id}
                    onClick={() =>
                      dispatch(
                        v3 && state.staffFilter === x.id
                          ? { type: 'FILTER_STAFF' }
                          : { type: 'FILTER_STAFF', staffId: x.id },
                      )
                    }
                  >
                    <Avatar person={x} size={v3 ? 112 : 72} />
                    <span className="v2-person-text">
                      <b>{x.displayName}</b>
                      {x.title && <small>{x.title}</small>}
                      {v3 && x.rating !== undefined && (
                        <span className="v3-rating" aria-label={`${x.rating} / 5 (${x.ratingCount})`}>
                          <i aria-hidden>★★★★★</i>
                          <b>{x.rating.toFixed(1)}</b>
                          <small>{REVIEWS[state.locale](x.ratingCount ?? 0)}</small>
                        </span>
                      )}
                    </span>
                  </button>
                ))}
              </div>
            )}
            {!date && <p className="v2-lead">{v.noTimesDay}</p>}
            {date && (
              <Load load={() => api.slots(locationId, [s.id], date)} m={m} deps={[locationId, s.id, date]}>
                {(slots) => {
                  const list = visibleSlots(slots, state.staffFilter);
                  if (list.length === 0) return <p className="v2-lead">{v.noTimesDay}</p>;
                  return (
                    <>
                      {slotsByPart(list).map(([part, xs]) => (
                        <section key={part} className="v2-part">
                          <h2>{v[part]}</h2>
                          <div className="v2-slots">
                            {xs.map((sl) => (
                              <button
                                key={`${sl.staffId}-${sl.start}`}
                                className={
                                  state.slot?.start === sl.start && state.slot.staffId === sl.staffId
                                    ? 'v2-slot on'
                                    : 'v2-slot'
                                }
                                onClick={() => dispatch({ type: 'PICK_SLOT', slot: sl })}
                              >
                                {sl.start.slice(11, 16)}
                                {state.staffFilter ? null : <small>{staffName(sl.staffId)}</small>}
                              </button>
                            ))}
                          </div>
                        </section>
                      ))}
                    </>
                  );
                }}
              </Load>
            )}
            <p className="v2-note">{v.reschedule}</p>
          </div>
        );
      }}
    </Load>
  );
}

const REASON: Record<ServiceKind, keyof V2Messages> = {
  face: 'forFace',
  combo: 'forFace',
  skin: 'forSkin',
  body: 'forBody',
  addon: 'forFace',
};

function ExtrasScreen({ state, dispatch, api, locationId, gifts, m, v }: ScreenProps) {
  const v3 = useV3();
  const mem = useContext(Mem);
  const [all, setAll] = useState(false);
  const s = state.service!;
  const slot = state.slot!;
  const { locale } = state;
  return (
    <Load
      load={async () => ({
        services: await api.services(locationId),
        room: await api
          .slotRoom(locationId, slot.staffId, slot.start, s.durationMin)
          .catch(() => ({ freeMinutes: null })),
      })}
      m={m}
      deps={[locationId, 'extras', slot.staffId, slot.start, s.id]}
    >
      {({ services, room }) => {
        const addons = addonsFor(services, slot.staffId);
        const chosen = addons.filter((a) => state.addonIds.includes(a.id));
        const chosenMin = chosen.reduce((t, a) => t + a.durationMin, 0);
        const left = room.freeMinutes === null ? null : room.freeMinutes - chosenMin;
        const suggested = suggestedExtras(addons, s, room.freeMinutes);
        // v3 (owner 23.10.2026): every extra that fits is on the screen at once, suggested first —
        // no "see all" step; the client scrolls on the iPad.
        const list = v3
          ? [...new Map([...suggested, ...addons].map((a) => [a.id, a])).values()]
          : all
            ? addons
            : [...new Map([...suggested, ...chosen].map((a) => [a.id, a])).values()];
        const reason = v[REASON[s.kind]] as string;
        const base =
          (coveredBy(mem, s) ? 0 : s.price.amountMinor) + chosen.reduce((t, a) => t + a.price.amountMinor, 0);
        return (
          <div className="v2-stack">
            <h1 className={v3 ? 'v2-h1 v3-red' : 'v2-h1'}>{v.extrasTitle}</h1>
            <p className="v2-lead">{v.extrasSub}</p>
            <div className="v2-extras">
              {list.map((a) => {
                const on = state.addonIds.includes(a.id);
                const fits = on || left === null || a.durationMin <= left;
                const full = !on && state.addonIds.length >= MAX_ADDONS;
                const button = v3 ? (
                  // Owner 31.10.2026 (board 13, H3): banner card — picture bleeds to the right edge,
                  // text fades over it; a picked card turns dark, no check mark, «Added» in the line.
                  <button
                    key={a.id}
                    className={on ? 'v2-extra v3-banner on' : 'v2-extra v3-banner'}
                    aria-pressed={on}
                    disabled={!fits || full}
                    onClick={() => dispatch({ type: 'TOGGLE_ADDON', id: a.id })}
                  >
                    {extraArt(a.title) && <img className="v3-banner-art" src={extraArt(a.title)} alt="" />}
                    <span className="v3-banner-fade" aria-hidden />
                    <span className="v2-extra-text">
                      <b>{localize(a.title, locale)}</b>
                      <span>
                        {fits
                          ? `${on ? `${v.added} · ` : ''}+${a.durationMin} ${m.minutes} · ${formatMoney(a.price, locale)}`
                          : m.cpNoTime}
                      </span>
                    </span>
                  </button>
                ) : (
                  <button
                    key={a.id}
                    className={on ? 'v2-extra on' : 'v2-extra'}
                    aria-pressed={on}
                    disabled={!fits || full}
                    onClick={() => dispatch({ type: 'TOGGLE_ADDON', id: a.id })}
                  >
                    <span className="v2-extra-text">
                      <b>{localize(a.title, locale)}</b>
                      <span>
                        {fits ? (suggested.includes(a) ? reason : `+${a.durationMin} ${m.minutes}`) : m.cpNoTime}
                      </span>
                    </span>
                    <span className="v2-extra-price">
                      +{a.durationMin} {m.minutes}
                      <b>{formatMoney(a.price, locale)}</b>
                    </span>
                    <span className="v2-tick" aria-hidden>
                      {on ? <CheckIcon size={22} /> : '+'}
                    </span>
                  </button>
                );
                return v3 ? (
                  <div key={a.id} className="v3-card-wrap">
                    {button}
                    <InfoButton service={a} locale={locale} />
                  </div>
                ) : (
                  button
                );
              })}
            </div>
            {!v3 && !all && addons.length > list.length && (
              <button className="v2-link" onClick={() => setAll(true)}>
                {v.showAllExtras(addons.length)}
              </button>
            )}
            <GiftBlock
              gifts={gifts}
              baseMinor={base}
              chosen={state.bookingGiftId}
              onPick={(giftId) => dispatch({ type: 'PICK_GIFT', giftId })}
              m={m}
              v={v}
              locale={locale}
            />
            <QuietProducts api={api} locationId={locationId} state={state} dispatch={dispatch} v={v} />
            {v3 && (
              // Owner 31.10 / 2.11.2026 (board 13d, T1): nothing picked → the red button reads
              // «I don’t feel like extras today» and goes on; something picked → «Yes, add LED · +€10»
              // goes on and a quiet «Change my mind» link clears the extras.
              <RailAction>
                <button className="v2-btn v2-btn-red" onClick={() => dispatch({ type: 'EXTRAS_DONE' })}>
                  {chosen.length > 0
                    ? v.yesAdd(
                        chosen.length,
                        localize(chosen[0]!.title, locale),
                        formatMoney(
                          {
                            amountMinor: chosen.reduce((t, a) => t + a.price.amountMinor, 0),
                            currency: s.price.currency,
                          },
                          locale,
                        ),
                      )
                    : state.bookingGiftId || state.productIds.length > 0
                      ? m.continue
                      : v.noExtrasToday}
                </button>
                {chosen.length > 0 && (
                  <button
                    className="v2-link v3-extras-no"
                    onClick={() => chosen.forEach((a) => dispatch({ type: 'TOGGLE_ADDON', id: a.id }))}
                  >
                    {v.changeMind}
                  </button>
                )}
              </RailAction>
            )}
          </div>
        );
      }}
    </Load>
  );
}

/**
 * Home-care products (owner 16.10.2026: keep them on the kiosk, without emphasis):
 * one quiet line that opens a short list. Nothing is pre-selected.
 */
function QuietProducts({
  api,
  locationId,
  state,
  dispatch,
  v,
}: {
  api: HubApi;
  locationId: string;
  state: V2State;
  dispatch: Dispatch;
  v: V2Messages;
}) {
  const [open, setOpen] = useState(state.productIds.length > 0);
  const [offers, setOffers] = useState<ProductOffer[]>([]);
  useEffect(() => {
    api.products(locationId).then(setOffers, () => setOffers([]));
  }, [api, locationId]);
  if (offers.length === 0) return null;
  return (
    <section className="v2-quiet">
      <button className="v2-link" aria-expanded={open} onClick={() => setOpen(!open)}>
        {v.productsToggle(offers.length)}
      </button>
      {open && (
        <div className="v2-quiet-list">
          {offers.map((p) => {
            const on = state.productIds.includes(p.id);
            return (
              <button
                key={p.id}
                className={on ? 'v2-quiet-item on' : 'v2-quiet-item'}
                aria-pressed={on}
                onClick={() => dispatch({ type: 'TOGGLE_PRODUCT', id: p.id })}
              >
                <span>
                  <b>{localize(p.title, state.locale)}</b>
                  {p.description && <small>{localize(p.description, state.locale)}</small>}
                </span>
                <span className="v2-quiet-price">{formatMoney(p.finalPrice, state.locale)}</span>
                <span className="v2-tick small" aria-hidden>
                  {on ? <CheckIcon size={18} /> : '+'}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}

/**
 * Gift for the booking amount: a progress line, and the choice once unlocked.
 * Owner 02.11.2026: the client must see that the gift is hers — once a gift is unlocked one is
 * always chosen (the first, until she picks another), shown as a picture card like the extras;
 * a gift that no longer fits the amount is swapped for one that does.
 */
function GiftBlock({
  gifts,
  baseMinor,
  chosen,
  onPick,
  m,
  v,
  locale,
}: {
  gifts: BookingGiftOption[];
  baseMinor: number;
  chosen?: string;
  onPick: (id?: string) => void;
  m: Messages;
  v: V2Messages;
  locale: Locale;
}) {
  const v3 = useV3();
  const open = eligibleGifts(gifts, baseMinor);
  const next = nextGiftStep(gifts, baseMinor);
  const openIds = open.map((g) => g.id).join(',');
  useEffect(() => {
    const ids = openIds ? openIds.split(',') : [];
    if (chosen && ids.includes(chosen)) return;
    const want = ids[0];
    if (want !== chosen) onPick(want);
  }, [openIds, chosen, onPick]);
  if (gifts.length === 0) return null;
  const pct = next ? Math.round((baseMinor / next.atMinor) * 100) : 100;
  const progress = next && (
    <>
      <p>
        {(open.length > 0 ? v.giftMore : v.giftProgress)(
          formatMoney({ amountMinor: next.missingMinor, currency: 'EUR' }, locale),
        )}
      </p>
      <div className="v2-meter" aria-hidden>
        <i style={{ width: `${pct}%` }} />
      </div>
    </>
  );
  if (v3) {
    return (
      <section className="v3-gifts">
        <div className="v3-gifts-head">
          <span className="v3-gifts-ico" aria-hidden>
            <GiftIcon size={34} />
          </span>
          <div>
            <h2>{open.length > 0 ? v.giftForYou : v.giftChoose}</h2>
            {progress}
          </div>
        </div>
        {open.map((g) => {
          const on = chosen === g.id;
          return (
            <button
              key={g.id}
              className={on ? 'v2-extra v3-banner v3-gift-card on' : 'v2-extra v3-banner v3-gift-card'}
              aria-pressed={on}
              onClick={() => onPick(g.id)}
            >
              <img className="v3-banner-art" src={giftPicture(g.id)} alt="" />
              <span className="v3-banner-fade" aria-hidden />
              <span className="v2-extra-text">
                <b>{localize(g.title, locale)}</b>
                <span>{on ? `🎁 ${v.giftYours}` : m.presentFree}</span>
              </span>
            </button>
          );
        })}
      </section>
    );
  }
  return (
    <section className="v2-gift">
      <span className="v2-gift-ico" aria-hidden>
        <GiftIcon />
      </span>
      <div>
        {progress}
        {open.length > 0 && (
          <>
            <p>
              <b>{v.giftChoose}</b> · {m.presentFree}
            </p>
            <div className="v2-filter">
              {open.map((g) => (
                <button key={g.id} aria-pressed={chosen === g.id} onClick={() => onPick(g.id)}>
                  {localize(g.title, locale)}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}

/** The gift's picture — the same 3D pictures as the extras. */
function giftPicture(id: string): string {
  if (id.includes('hand')) return '/v3/x-hand.jpg';
  if (id.includes('led')) return '/v3/x-led.jpg';
  if (id.includes('mask')) return '/v3/x-mask.jpg';
  return '/v3/gift.png';
}

/**
 * Details (owner 17.10.2026): a polite title, the phone first. A returning client is recognised
 * by the number — "Is this you, Ana M.?" with a masked e-mail — and then types nothing else.
 * "That's not me" (or a new number) shows the usual name and e-mail fields.
 */
/**
 * «Continue» on the details with something missing (owner 24.10.2026: on a phone the consent box and
 * its message sat under the keyboard, so the button looked dead): close the keyboard and bring the
 * first problem — the «Is this you?» question, a red field or the consent — into view.
 */
function showContactProblem() {
  (document.activeElement as HTMLElement | null)?.blur?.();
  window.setTimeout(() => {
    const el = document.querySelector<HTMLElement>(
      '.v2-form .v2-known:not(.done), .v2-form .invalid, .v2-form .v2-err, .v2-form .v2-field em',
    );
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, 80);
}

function ContactScreen({ state, dispatch, api, locationId, m, v, contactTouched: touched }: ScreenProps) {
  const c = state.customer;
  const errors = contactErrors(state);
  const show = (f: CustomerField) => touched && errors.includes(f);
  const e164 = toE164(c.phoneCountry, c.phone);
  const known = state.known?.phone === e164 ? state.known : undefined;
  const view = known?.view;
  const yes = knownYes(state);
  const asking = Boolean(view?.known && !known?.answer);
  const closeKeyboard = useKeyboardClose();
  const answer = (a: 'yes' | 'no') => {
    if (a === 'yes') closeKeyboard();
    dispatch({ type: 'KNOWN_ANSWER', answer: a });
  };

  // look the number up once it is complete (and only once per number)
  useEffect(() => {
    if (!e164 || state.known?.phone === e164) return;
    let live = true;
    const t = window.setTimeout(() => {
      api
        .recognize(locationId, e164)
        .then((r) => live && dispatch({ type: 'RECOGNIZED', phone: e164, view: r }))
        .catch(() => live && dispatch({ type: 'RECOGNIZED', phone: e164, view: { known: false } }));
    }, 350);
    return () => {
      live = false;
      window.clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [e164, api, locationId]);

  const field = (f: 'fullName' | 'email') => (
    <label className="v2-field" key={f}>
      <span>{m.fields[f]}</span>
      <KInput
        mode={f === 'email' ? 'email' : 'text'}
        max={f === 'email' ? 120 : 80}
        className={show(f) ? 'v2-input invalid' : 'v2-input'}
        autoCapitalize={f === 'fullName' ? 'words' : 'off'}
        value={c[f]}
        onValue={(x) => dispatch({ type: 'EDIT_CUSTOMER', patch: { [f]: x } })}
      />
      {show(f) && <em>{m.fieldErrors[f]}</em>}
    </label>
  );
  const check = (f: 'privacy' | 'marketing', text: string) => (
    <button
      type="button"
      className="v2-check"
      role="checkbox"
      aria-checked={c[f]}
      onClick={() => dispatch({ type: 'EDIT_CUSTOMER', patch: { [f]: !c[f] } })}
    >
      <span className="v2-box" aria-hidden>
        {c[f] ? <CheckIcon size={20} /> : null}
      </span>
      <span>{text}</span>
    </button>
  );
  const privacyNeeded = !(yes && view?.privacyOnFile);
  return (
    <div className="v2-stack v2-form">
      <V3Head art={V3_ART.card}>
        <h1 className="v2-h1">{v.detailsTitle}</h1>
      </V3Head>
      <div className="v2-field">
        <span>{m.fields.phone}</span>
        <PhoneInput
          country={c.phoneCountry}
          value={c.phone}
          invalid={show('phone')}
          onChange={(p) =>
            dispatch({
              type: 'EDIT_CUSTOMER',
              patch: {
                ...(p.country ? { phoneCountry: p.country } : {}),
                ...(p.phone !== undefined ? { phone: p.phone } : {}),
              },
            })
          }
          locale={state.locale}
          m={m}
        />
        {show('phone') && <em>{m.fieldErrors.phone}</em>}
      </div>
      <KeyboardSlot labels={{ done: m.kbDone, space: m.kbSpace }} inline />

      {asking && view && (
        <div className="v2-card v2-known" role="group" aria-label={v.isThisYou(view.name ?? '')}>
          <b>{v.isThisYou(view.name ?? '')}</b>
          <span>{view.emailMask ?? v.knownSub}</span>
          <div className="v2-pair">
            <button className="v2-btn v2-btn-red" onClick={() => answer('yes')}>
              {v.itsMe}
            </button>
            <button className="v2-btn v2-btn-quiet" onClick={() => answer('no')}>
              {v.notMe}
            </button>
          </div>
        </div>
      )}

      {yes && view && (
        <div className="v2-card v2-known done">
          <span className="v2-known-mark" aria-hidden>
            <CheckIcon size={22} />
          </span>
          <div>
            <b>{view.name}</b>
            <span>{view.emailMask ?? v.knownEmailNeeded}</span>
          </div>
          <button className="v2-link" onClick={() => answer('no')}>
            {v.notMe}
          </button>
        </div>
      )}
      {yes && view && !view.emailMask && field('email')}

      {!asking && !yes && (
        <>
          {field('fullName')}
          {field('email')}
        </>
      )}

      {!asking && privacyNeeded && check('privacy', m.privacy)}
      {!asking && privacyNeeded && show('privacy') && <em className="v2-err">{m.fieldErrors.privacy}</em>}
      {!asking && check('marketing', m.marketing)}
      {!asking && <p className="v2-note">{m.nifNote}</p>}
    </div>
  );
}

function ConfirmScreen({ state, dispatch, api, locationId, staff, staffName, gifts, m, v }: ScreenProps) {
  const { locale } = state;
  const mem = useContext(Mem);
  const s = state.service!;
  const slot = state.slot!;
  return (
    <Load
      load={async () => ({
        services: await api.services(locationId),
        products: await api.products(locationId).catch(() => []),
      })}
      m={m}
      deps={[locationId, 'confirm']}
    >
      {({ services, products }) => {
        const addons = state.addonIds
          .map((id) => services.find((x) => x.id === id))
          .filter((x): x is Service => Boolean(x));
        const items = [s, ...addons];
        const servicesMinor = items.reduce((t, x) => t + (coveredBy(mem, x) ? 0 : x.price.amountMinor), 0);
        const chosenProducts = state.productIds
          .map((id) => products.find((p) => p.id === id))
          .filter((p): p is ProductOffer => Boolean(p));
        const total = servicesMinor + chosenProducts.reduce((t, p) => t + p.finalPrice.amountMinor, 0);
        const minutes = items.reduce((t, x) => t + x.durationMin, 0);
        const gift = eligibleGifts(gifts, servicesMinor).find((g) => g.id === state.bookingGiftId);
        return (
          <div className="v2-stack">
            <h1 className="v2-h1">{v.confirmTitle}</h1>
            <div className="v2-card v2-review">
              <div className="v2-review-when">
                <div className="v2-review-time">
                  <span className="v2-big-time">{slot.start.slice(11, 16)}</span>
                  <b>{formatDayLong(slot.start.slice(0, 10), locale)}</b>
                  <span>
                    {slot.start.slice(11, 16)}–{endTime(slot.start, minutes)} · {m.with} {staffName(slot.staffId)}
                  </span>
                </div>
                <div className="v2-review-staff">
                  <Avatar person={staff.find((x) => x.id === slot.staffId)} size={72} />
                  <button className="v2-link" onClick={() => dispatch({ type: 'GOTO', step: 'time' })}>
                    {v.edit}
                  </button>
                </div>
              </div>
              <div className="v2-rows">
                {items.map((x) => (
                  <div className="v2-row" key={x.id}>
                    <span>{localize(x.title, locale)}</span>
                    <span>{coveredBy(mem, x) ? v.memRow : formatMoney(x.price, locale)}</span>
                  </div>
                ))}
                {chosenProducts.map((p) => (
                  <div className="v2-row" key={p.id}>
                    <span>{localize(p.title, locale)}</span>
                    <span>{formatMoney(p.finalPrice, locale)}</span>
                  </div>
                ))}
                {gift && (
                  <div className="v2-row">
                    <span>
                      {m.presentLabel}: {localize(gift.title, locale)}
                    </span>
                    <span>{m.presentFree}</span>
                  </div>
                )}
                <div className="v2-row total">
                  <span>{m.total}</span>
                  <span>{formatMoney({ amountMinor: total, currency: s.price.currency }, locale)}</span>
                </div>
              </div>
              <div className="v2-review-who">
                <span>
                  {knownYes(state)
                    ? `${state.known!.view.name} · ${state.known!.view.emailMask ?? state.customer.email}`
                    : `${state.customer.fullName} · ${state.customer.email}`}
                </span>
                <button className="v2-link" onClick={() => dispatch({ type: 'GOTO', step: 'contact' })}>
                  {v.edit}
                </button>
              </div>
            </div>
            <label className="v2-field v2-comment">
              <span>{v.commentLabel}</span>
              <KInput
                mode="text"
                multiline
                rows={2}
                max={300}
                className="v2-input"
                placeholder={v.commentHint}
                value={state.comment ?? ''}
                onValue={(x) => dispatch({ type: 'EDIT_COMMENT', comment: x })}
              />
            </label>
            <KeyboardSlot labels={{ done: m.kbDone, space: m.kbSpace }} inline />
            <p className="v2-note">{v.reschedule}</p>
          </div>
        );
      }}
    </Load>
  );
}

function FindScreen({ state, dispatch, api, locationId, m, v, callStaff, called, remote }: ScreenProps) {
  const e164 = toE164(state.country, state.phone);
  const goCall = () => {
    callStaff('checkin_not_found');
    dispatch({ type: 'DISMISS_MISS' });
  };
  return (
    <div className="v2-stack">
      <V3Head art={V3_ART.phone}>
        <h1 className="v2-h1">{v.findTitle}</h1>
        <p className="v2-lead">{remote ? v.remoteFindSub : v.findSub}</p>
      </V3Head>
      <PhoneInput
        big
        country={state.country}
        value={state.phone}
        onChange={(p) => dispatch({ type: 'EDIT_PHONE', ...p })}
        locale={state.locale}
        m={m}
      />
      <KeyboardSlot labels={{ done: m.kbDone, space: m.kbSpace }} inline />
      <RailAction>
        <SubmitButton
          className="v2-btn v2-btn-red"
          label={m.checkinFind}
          busyLabel={m.loading}
          m={m}
          disabled={!e164}
          run={async () =>
            dispatch({
              type: 'MATCHES',
              matches: remote
                ? await api.prepareLookup(locationId, e164!)
                : await api.lookupBookings(locationId, { phone: e164! }),
            })
          }
        />
      </RailAction>
      {!remote && (
        <div className="v2-find-alt">
          <div className="v2-card v2-scan">
            <QrIcon />
            <span>{m.scanHint}</span>
          </div>
          <button className="v2-card v2-cp" onClick={() => dispatch({ type: 'GO_CLASSPASS' })}>
            <b>ClassPass</b>
            <span>{v.findClassPass}</span>
          </button>
        </div>
      )}
      {state.miss && remote && (
        <div className="v2-backdrop">
          <div className="v2-sheet" role="alertdialog" aria-modal="true" aria-label={m.notFoundTitle}>
            <h2>{m.notFoundTitle}</h2>
            <p>{v.remoteNotFound}</p>
            <div className="v2-sheet-grid">
              <button className="v2-btn v2-btn-red" onClick={() => dispatch({ type: 'DISMISS_MISS' })}>
                {m.tryAgain}
              </button>
            </div>
          </div>
        </div>
      )}
      {state.miss && !remote && (
        <div className="v2-backdrop">
          <div className="v2-sheet" role="alertdialog" aria-modal="true" aria-label={m.notFoundTitle}>
            <h2>{m.notFoundTitle}</h2>
            <p>{state.card ? m.cardNoBooking : state.misses >= 2 ? m.notFoundAgain : m.notFoundText}</p>
            <div className="v2-sheet-grid">
              {state.misses >= 2 ? (
                <>
                  <button className="v2-btn v2-btn-red" disabled={called} onClick={goCall}>
                    {m.callAdmin}
                  </button>
                  <button className="v2-btn v2-btn-quiet" onClick={() => dispatch({ type: 'DISMISS_MISS' })}>
                    {m.tryAgain}
                  </button>
                </>
              ) : (
                <>
                  <button className="v2-btn v2-btn-red" onClick={() => dispatch({ type: 'DISMISS_MISS' })}>
                    {m.tryAgain}
                  </button>
                  <button className="v2-btn v2-btn-quiet" disabled={called} onClick={goCall}>
                    {m.callAdmin}
                  </button>
                </>
              )}
              <button className="v2-btn v2-btn-quiet" onClick={() => dispatch({ type: 'RESET' })}>
                {m.checkinBook}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Check-in: at most one suggestion (Zenoti: one contextual question), only if it fits before
 * the therapist's next booking, plus the gift for the booking amount when unlocked.
 * "No, thank you" weighs the same as "Add". Nothing to offer → straight on.
 */
function OfferScreen({ state, dispatch, api, locationId, gifts, m, v }: ScreenProps) {
  const visit = state.visit!;
  const { locale } = state;
  const [giftId, setGiftId] = useState<string>();
  const [add, setAdd] = useState(false);
  const v3 = useV3();
  return (
    <Load
      load={async () => ({
        services: await api.services(locationId),
        free: await api.freeMinutes(visit).catch(() => ({ freeMinutes: 0 })),
      })}
      m={m}
      deps={[visit.id, 'offer']}
    >
      {({ services, free }) => {
        const main = services.find((s) => visit.lines.some((l) => l.kind === 'service' && l.serviceId === s.id));
        const has = new Set(visit.lines.map((l) => l.serviceId));
        // only while the visit can still change (not after the treatment), only what this therapist
        // does and only what fits before her next booking (owner 16.10.2026)
        const editable = visit.state === 'booked' || visit.state === 'checked_in';
        const offer =
          main && editable && free.freeMinutes !== undefined
            ? suggestedExtras(
                addonsFor(services, visit.staffId).filter((a) => !has.has(a.id)),
                main,
                free.freeMinutes,
                1,
              )[0]
            : undefined;
        const base = giftBaseMinor(
          visit.lines.map((l) => ({ kind: l.kind, unitPriceMinor: l.unitPriceMinor, ordered: l.quantity })),
        );
        const openGifts = visit.bookingGift ? [] : eligibleGifts(gifts, base);
        const finish = async (addId?: string) => {
          let next: KioskVisit | undefined;
          if (addId) next = await api.addAddons(visit, [addId]);
          if (giftId) next = await api.chooseGift(next ?? visit, giftId);
          if (state.productIds.length > 0) next = await api.addProducts(next ?? visit, state.productIds);
          dispatch({ type: 'OFFER_DONE', visit: next });
        };
        return (
          <OfferBody nothing={!offer && openGifts.length === 0} onSkip={() => dispatch({ type: 'OFFER_DONE' })}>
            <div className="v2-stack">
              <h1 className="v2-h1">{v.offerTitle}</h1>
              {/* owner 17.10.2026: one tap to add (or not), then "Continue" in the bottom panel */}
              {offer && (
                <button className={add ? 'v2-extra on' : 'v2-extra'} aria-pressed={add} onClick={() => setAdd(!add)}>
                  {v3 && (
                    <span className="v3-extra-art" aria-hidden>
                      {extraArt(offer.title) && <img src={extraArt(offer.title)} alt="" />}
                    </span>
                  )}
                  <span className="v2-extra-text">
                    <b>{localize(offer.title, locale)}</b>
                    <span>{v[REASON[main!.kind]] as string}</span>
                  </span>
                  <span className="v2-extra-price">
                    +{offer.durationMin} {m.minutes}
                    <b>{formatMoney(offer.price, locale)}</b>
                  </span>
                  <span className="v2-tick" aria-hidden>
                    {add ? <CheckIcon size={22} /> : '+'}
                  </span>
                </button>
              )}
              {openGifts.length > 0 && (
                <GiftBlock
                  gifts={gifts}
                  baseMinor={base}
                  chosen={giftId}
                  onPick={setGiftId}
                  m={m}
                  v={v}
                  locale={locale}
                />
              )}
              <QuietProducts api={api} locationId={locationId} state={state} dispatch={dispatch} v={v} />
              <RailAction>
                <SubmitButton
                  className="v2-btn v2-btn-red"
                  label={m.continue}
                  busyLabel={m.sending}
                  m={m}
                  run={() => finish(add && offer ? offer.id : undefined)}
                />
              </RailAction>
            </div>
          </OfferBody>
        );
      }}
    </Load>
  );
}

function OfferBody({ nothing, onSkip, children }: { nothing: boolean; onSkip: () => void; children: ReactNode }) {
  useEffect(() => {
    if (nothing) onSkip();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nothing]);
  return nothing ? null : <>{children}</>;
}

function GiftScreen({ state, dispatch, api, locationId, m, v }: ScreenProps) {
  return (
    <Load load={() => api.giftOptions(locationId)} m={m} deps={[locationId, 'gift']}>
      {(options) => (
        <div className="v2-stack">
          <V3Head art={V3_ART.gift}>
            <h1 className="v2-h1">{m.giftTitle}</h1>
            <p className="v2-lead">{m.giftSub}</p>
          </V3Head>
          <h2 className="v2-h2">{v.giftValueTitle}</h2>
          <div className="v2-values">
            {options.map((g) => (
              <button
                key={g.id}
                aria-pressed={state.giftValueId === g.id}
                onClick={() => dispatch({ type: 'GIFT_VALUE', giftId: g.id })}
              >
                {formatMoney(g.value, state.locale)}
              </button>
            ))}
          </div>
          <h2 className="v2-h2">{v.giftHowShort}</h2>
          <div className="v2-pair">
            {(['pickup', 'email'] as const).map((d) => (
              <button
                key={d}
                className="v2-option"
                aria-pressed={state.giftDelivery === d}
                onClick={() => dispatch({ type: 'GIFT_DELIVERY', delivery: d })}
              >
                <b>{d === 'pickup' ? m.giftPickup : m.giftEmail}</b>
                <span>{d === 'pickup' ? m.giftPickupSub : m.giftEmailSub}</span>
              </button>
            ))}
          </div>
          <RailAction>
            <button
              className="v2-btn v2-btn-red"
              disabled={!state.giftValueId || !state.giftDelivery}
              onClick={() => dispatch({ type: 'GIFT_NEXT' })}
            >
              {m.continue}
            </button>
          </RailAction>
        </div>
      )}
    </Load>
  );
}

function GiftPhoneScreen({ state, dispatch, api, locationId, m }: ScreenProps) {
  const e164 = toE164(state.customer.phoneCountry, state.customer.phone);
  const byEmail = state.giftDelivery === 'email';
  const emailOk = !byEmail || /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(state.giftEmail.trim());
  return (
    <div className="v2-stack v2-form">
      <V3Head art={V3_ART.phone}>
        <h1 className="v2-h1">{m.giftPhoneTitle}</h1>
        <p className="v2-lead">{m.giftPhoneSub}</p>
      </V3Head>
      <PhoneInput
        big
        country={state.customer.phoneCountry}
        value={state.customer.phone}
        onChange={(p) => dispatch({ type: 'EDIT_GIFT_PHONE', ...p })}
        locale={state.locale}
        m={m}
      />
      <KeyboardSlot labels={{ done: m.kbDone, space: m.kbSpace }} inline />
      {byEmail && (
        <label className="v2-field">
          <span>{m.giftEmailField}</span>
          <KInput
            mode="email"
            max={120}
            className="v2-input"
            type="email"
            autoCapitalize="none"
            value={state.giftEmail}
            onValue={(email) => dispatch({ type: 'EDIT_GIFT_EMAIL', email })}
          />
        </label>
      )}
      <RailAction>
        {byEmail ? (
          <button
            className="v2-btn v2-btn-red"
            disabled={!e164 || !emailOk}
            onClick={() => dispatch({ type: 'GIFT_NEXT' })}
          >
            {m.continue}
          </button>
        ) : (
          <SubmitButton
            className="v2-btn v2-btn-red"
            label={m.buy}
            busyLabel={m.sending}
            m={m}
            disabled={!e164}
            run={async () =>
              dispatch({
                type: 'GIFT_CREATED',
                visit: await api.buyGift(locationId, state.idempotencyKey, state.giftValueId!, e164!, 'pickup'),
              })
            }
          />
        )}
      </RailAction>
    </div>
  );
}

function GiftCardScreen({ state, dispatch, api, locationId, m }: ScreenProps) {
  const card = state.giftCard;
  const e164 = toE164(state.customer.phoneCountry, state.customer.phone);
  const field = (f: 'to' | 'from' | 'message', label: string, max: number) => (
    <label className="v2-field" key={f}>
      <span>{label}</span>
      <KInput
        mode="text"
        max={max}
        className="v2-input"
        type="text"
        value={card[f]}
        onValue={(value) => dispatch({ type: 'EDIT_GIFT_CARD', field: f, value })}
      />
    </label>
  );
  return (
    <div className="v2-stack v2-form">
      <h1 className="v2-h1">{m.giftCardTitle}</h1>
      <p className="v2-lead">{m.giftCardSub}</p>
      {field('to', m.giftTo, 60)}
      {field('from', m.giftFrom, 60)}
      {field('message', m.giftMessage, 300)}
      <RailAction>
        <SubmitButton
          className="v2-btn v2-btn-red"
          label={m.giftSend}
          busyLabel={m.sending}
          m={m}
          disabled={!e164 || !card.to.trim() || !card.from.trim()}
          run={async () =>
            dispatch({
              type: 'GIFT_CREATED',
              visit: await api.buyGift(
                locationId,
                state.idempotencyKey,
                state.giftValueId!,
                e164!,
                'email',
                state.giftEmail.trim(),
                {
                  to: card.to.trim(),
                  from: card.from.trim(),
                  message: card.message.trim() || undefined,
                },
              ),
            })
          }
        />
      </RailAction>
    </div>
  );
}

/** Same rule as v1: mandatory question; only the answer is sent, the form goes encrypted. */
function QuestionnaireScreen({ state, dispatch, api, locationId, m }: ScreenProps) {
  const [answer, setAnswer] = useState<'yes' | 'no'>();
  const [confirmed, setConfirmed] = useState(false);
  const [filling, setFilling] = useState(false);
  const send = (a: 'confirmed' | 'needed') => async () =>
    dispatch({ type: 'QUESTIONNAIRE_DONE', visit: state.visit ? await api.questionnaire(state.visit, a) : undefined });
  if (filling && state.visit)
    return (
      <div className="v2-qform">
        <ClientForm
          submit={({ label, run }) => (
            <SubmitButton className="v2-btn v2-btn-red v2-wide" label={label} busyLabel={m.sending} m={m} run={run} />
          )}
          visit={state.visit}
          initialName={state.mode === 'booking' ? state.customer.fullName : ''}
          locale={state.locale}
          api={api}
          locationId={locationId}
          m={m}
          onCancel={() => setFilling(false)}
          onDone={(visit) => dispatch({ type: 'QUESTIONNAIRE_DONE', visit })}
        />
      </div>
    );
  return (
    <div className="v2-stack">
      <h1 className="v2-h1">{m.qTitle}</h1>
      <p className="v2-lead">{m.qSub}</p>
      <div className="v2-pair">
        <button className="v2-option" aria-pressed={answer === 'yes'} onClick={() => setAnswer('yes')}>
          <b>{m.qYes}</b>
        </button>
        <button className="v2-option" aria-pressed={answer === 'no'} onClick={() => setAnswer('no')}>
          <b>{m.qNo}</b>
        </button>
      </div>
      {answer === 'yes' && (
        <>
          <button
            type="button"
            className="v2-check"
            role="checkbox"
            aria-checked={confirmed}
            onClick={() => setConfirmed(!confirmed)}
          >
            <span className="v2-box" aria-hidden>
              {confirmed ? <CheckIcon size={20} /> : null}
            </span>
            <span>{m.qConfirm}</span>
          </button>
          <RailAction>
            <SubmitButton
              className="v2-btn v2-btn-red"
              label={m.continue}
              busyLabel={m.sending}
              m={m}
              disabled={!confirmed}
              run={send('confirmed')}
            />
          </RailAction>
        </>
      )}
      {answer === 'no' && (
        <>
          <p className="v2-card">{m.qfOffer}</p>
          {state.visit && (
            <button className="v2-btn v2-btn-red v2-wide" onClick={() => setFilling(true)}>
              {m.qfStart}
            </button>
          )}
          <SubmitButton
            className="v2-btn v2-btn-quiet v2-wide"
            label={m.qfPaper}
            busyLabel={m.sending}
            m={m}
            run={send('needed')}
          />
          <p className="v2-note">{m.qNeeded}</p>
        </>
      )}
    </div>
  );
}

function DoneScreen(p: ScreenProps) {
  return p.remote ? <RemoteDone {...p} /> : <KioskDone {...p} />;
}

/** Phone page: everything is chosen; arrival is one tap on the kiosk. No countdown, nothing resets. */
function RemoteDone({ state, staffName, m, v }: ScreenProps) {
  const visit = state.visit!;
  const { locale } = state;
  return (
    <div className="v2-stack v2-center v2-done">
      <span className="v2-done-mark" aria-hidden>
        <CheckIcon size={48} />
      </span>
      <h1 className="v2-h1">{v.remoteDoneTitle}</h1>
      <p className="v2-lead">{v.remoteDoneText(visit.start.slice(11, 16))}</p>
      <BookingLine visit={visit} m={m} locale={locale} />
      <p className="v2-done-facts">
        {formatDay(visit.start.slice(0, 10), locale)}
        {staffName(visit.staffId) && ` · ${staffName(visit.staffId)}`}
        {visit.payment.method && ` · ${m.methods[visit.payment.method]}`}
      </p>
    </div>
  );
}

function KioskDone({ state, staffName, m, v, onNew, locationId }: ScreenProps) {
  const visit = state.visit!;
  const { locale } = state;
  // On the client's own phone (not the studio kiosk) a new booking can go to the phone's calendar,
  // and the screen does not close by itself (owner 26.10.2026).
  const calendar = state.mode !== 'gift' && state.mode !== 'checkin' && state.mode !== 'classpass' && !isStudioKiosk();
  const left = useCountdown(calendar ? 24 * 3600 : DONE_SECONDS, onNew);
  const studio = STUDIOS.find((x) => x.id === locationId);
  let text: string;
  if (state.mode === 'gift')
    text = (visit.giftDelivery === 'email' ? m.doneGiftEmail : m.doneGift)(
      formatMoney({ amountMinor: visit.totalMinor, currency: 'EUR' }, locale),
    );
  else if (state.mode === 'checkin' || state.mode === 'classpass')
    text = staffName(visit.staffId) ? v.doneArrived(staffName(visit.staffId)) : v.doneArrivedNoStaff;
  else if (state.future)
    text = `${visit.bookingStatus === 'crm_booked' ? m.doneBooked : m.doneRequested} ${v.doneBookedFuture}`;
  else text = visit.bookingStatus === 'crm_booked' ? m.doneBooked : m.doneRequested;
  return (
    <div className="v2-stack v2-center v2-done">
      <span className="v2-done-mark" aria-hidden>
        {state.mode === 'gift' ? <GiftIcon size={48} /> : <CheckIcon size={48} />}
      </span>
      <h1 className="v2-h1">{state.mode === 'gift' ? m.thanks : m.doneTitle(visit.firstName)}</h1>
      <p className="v2-lead">{text}</p>
      {state.mode !== 'gift' && <BookingLine visit={visit} m={m} locale={locale} />}
      {state.mode !== 'gift' && (
        <p className="v2-done-facts">
          {formatDay(visit.start.slice(0, 10), locale)}
          {staffName(visit.staffId) && ` · ${staffName(visit.staffId)}`}
          {visit.payment.method && ` · ${m.methods[visit.payment.method]}`}
        </p>
      )}
      {calendar && (
        <a
          className="v2-btn v2-btn-red"
          href={icsHref({
            id: visit.id,
            start: visit.start,
            minutes: visit.durationMin,
            title: `360°FACE · ${visit.lines.map((l) => l.title).join(' + ')}`,
            location: studio && `360°FACE, ${studio.address}`,
            url: studio && mapsUrl(studio.address),
            description: [
              staffName(visit.staffId) && `${m.with} ${staffName(visit.staffId)}`,
              studio && `Google Maps: ${mapsUrl(studio.address)}`,
            ]
              .filter(Boolean)
              .join('\n'),
          })}
          download="360face.ics"
        >
          {v.addToCalendar}
        </a>
      )}
      <button className="v2-btn v2-btn-ink" onClick={onNew}>
        {m.doneNewCustomer}
      </button>
      {!calendar && <p className="v2-note">{v.closesIn(left)}</p>}
    </div>
  );
}

function useCountdown(seconds: number, onEnd: () => void): number {
  const [left, setLeft] = useState(seconds);
  const end = useRef(onEnd);
  end.current = onEnd;
  useEffect(() => {
    const t = window.setInterval(() => setLeft((x) => x - 1), 1000);
    return () => window.clearInterval(t);
  }, []);
  useEffect(() => {
    if (left <= 0) end.current();
  }, [left]);
  return Math.max(0, left);
}

/**
 * Inactivity (WCAG 2.2.1): after IDLE_WARN_SECONDS a warning with IDLE_GRACE_SECONDS to stay;
 * then a full reset that forgets the customer. Any touch before the warning restarts the timer.
 */
function useIdle(active: boolean, onTimeout: () => void): { warning?: number; stay: () => void } {
  const [warning, setWarning] = useState<number>();
  const [epoch, setEpoch] = useState(0);
  const timeout = useRef(onTimeout);
  timeout.current = onTimeout;
  useEffect(() => {
    setWarning(undefined);
    if (!active) return;
    let warnTimer = window.setTimeout(() => setWarning(IDLE_GRACE_SECONDS), IDLE_WARN_SECONDS * 1000);
    const bump = () => {
      window.clearTimeout(warnTimer);
      warnTimer = window.setTimeout(() => setWarning(IDLE_GRACE_SECONDS), IDLE_WARN_SECONDS * 1000);
    };
    window.addEventListener('pointerdown', bump);
    window.addEventListener('keydown', bump);
    return () => {
      window.clearTimeout(warnTimer);
      window.removeEventListener('pointerdown', bump);
      window.removeEventListener('keydown', bump);
    };
  }, [active, epoch]);
  useEffect(() => {
    if (warning === undefined) return;
    if (warning <= 0) {
      setWarning(undefined);
      timeout.current();
      return;
    }
    const t = window.setTimeout(() => setWarning((w) => (w === undefined ? w : w - 1)), 1000);
    return () => window.clearTimeout(t);
  }, [warning]);
  return {
    warning,
    stay: () => {
      setWarning(undefined);
      setEpoch((e) => e + 1);
    },
  };
}

/** QR / barcode scanner in the stand while the "find" screen is open (same as v1). */
function useCardScanner(api: HubApi, locationId: string, state: V2State, dispatch: Dispatch) {
  const active = state.step === 'find' && !state.remote;
  const phone = useRef(state.phone);
  phone.current = state.phone;
  useEffect(() => {
    if (!active) return;
    const buffer = createScanBuffer();
    let before = '';
    const onKey = (e: KeyboardEvent) => {
      const r = buffer.push(e.key, e.timeStamp);
      if (r.started) before = phone.current;
      if (!r.code) return;
      e.preventDefault();
      const code = r.code;
      dispatch({ type: 'EDIT_PHONE', phone: before });
      dispatch({ type: 'CARD_SCANNED', code });
      api
        .lookupBookings(locationId, { cardCode: code })
        .then((matches) => dispatch({ type: 'MATCHES', matches }))
        .catch(() => dispatch({ type: 'MATCHES', matches: [] }));
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [active, api, locationId, dispatch]);
}
